// src/context/AuthContext.js
import { createContext, useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { setAuthLogout } from "../api/axios";
export const AuthContext = createContext();

export default function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authenticationInProgress, setAuthenticationInProgress] = useState(false);
  const { instance } = useMsal();

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("azure_token");

    if (storedUser && token) {
      // Validate token is not expired before setting user
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Date.now() / 1000;
        
        if (payload.exp && payload.exp > now) {
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);
        } else {
          // Token expired, clear storage
          localStorage.removeItem("user");
          localStorage.removeItem("azure_token");
          setIsAuthenticated(false);
        }
      } catch (e) {
        // Invalid token, clear storage
        localStorage.removeItem("user");
        localStorage.removeItem("azure_token");
        setIsAuthenticated(false);
      }
    } else {
      setIsAuthenticated(false);
    }

    setLoading(false);
  }, []);

  const login = (userData, accessToken) => {
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("azure_token", accessToken);
    setUser(userData);
    setIsAuthenticated(true);
    setAuthenticationInProgress(false);
  };

  const logout = useCallback((showRedirect = true) => {
    // Clear local storage
    localStorage.removeItem("user");
    localStorage.removeItem("azure_token");
    
    // Clear user state
    setUser(null);
    setIsAuthenticated(false);
    setAuthenticationInProgress(false);

    // Clear MSAL account & token caches
    try {
      instance.clearCache();
      instance.setActiveAccount(null);
    } catch (e) {
      console.error("Error clearing MSAL cache:", e);
    }
    
    // Redirect to login if requested
    if (showRedirect) {
      window.location.href = "/login";
    }
  }, [instance]);

  const setAuthInProgress = useCallback((inProgress) => {
    setAuthenticationInProgress(inProgress);
  }, []);

  // Register logout function with axios interceptor
  useEffect(() => {
    setAuthLogout(logout);
  }, [logout]);

  // Function to update user after token refresh
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      isAuthenticated, 
      authenticationInProgress, 
      login, 
      logout, 
      updateUser, 
      setAuthInProgress 
    }}>
      {children}
    </AuthContext.Provider>
  );
}
